import React, { useCallback, useMemo, useState } from "react";

import Box from "@mui/material/Box";
import Radio from "@mui/material/Radio";
import FormGroup from "@mui/material/FormGroup";
import Typography from "@mui/material/Typography";
import FormControlLabel from "@mui/material/FormControlLabel";

import BaseFilter from "../BaseFilter";
import DateRangePicker from "components/DateRangePicker";

import { DateRangeValue } from "components/DateRangePicker/types";
import {
  DateRangeFilterValue,
  DateRangeOption,
  DateRangeType,
} from "components/TableFilters/types";

import dayjs from "dayjs";

interface DateRangeFilterProps {
  id: string;
  label: string;
  options: DateRangeOption[];
  activeFilter: DateRangeFilterValue;
  onChange: (activeFilter: DateRangeFilterValue | null) => void;
}

const getOptionRange = (type: DateRangeType) => {
  switch (type) {
    case DateRangeType.ALL_TIME:
      return { from: null, to: null };
    case DateRangeType.LAST_7_DAYS:
      return {
        from: dayjs().subtract(7, "day").startOf("day"),
        to: dayjs().endOf("day"),
      };
    case DateRangeType.LAST_30_DAYS:
      return {
        from: dayjs().subtract(30, "day").startOf("day"),
        to: dayjs().endOf("day"),
      };
    case DateRangeType.LAST_90_DAYS:
      return {
        from: dayjs().subtract(90, "day").startOf("day"),
        to: dayjs().endOf("day"),
      };
    case DateRangeType.CUSTOM_PERIOD:
      return { from: null, to: null };
    case DateRangeType.CUSTOM_VALUE:
      return null;
    default:
      return { from: null, to: null };
  }
};

const DateRangeFilter: React.FC<DateRangeFilterProps> = ({
  id,
  label,
  activeFilter,
  options,
  onChange,
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  const openPopover = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const closePopover = () => {
    setAnchorEl(null);
  };

  const activeOptionId = useMemo(() => activeFilter.id, [activeFilter.id]);

  const datePickerValue = useMemo(
    () =>
      activeOptionId === "date_range"
        ? activeFilter.range
        : { from: null, to: null },
    [activeFilter]
  );

  const handleRadioChange = useCallback(
    (option: DateRangeOption) => {
      const range = getOptionRange(option.type);

      if (option.type === DateRangeType.CUSTOM_PERIOD) {
        onChange({ id: option.id, range: activeFilter.range });
      } else if (!range) {
        onChange({ id: option.id, ...option.value });
        closePopover();
      } else {
        onChange({ id: option.id, range });
        closePopover();
      }
    },
    [activeFilter, onChange]
  );

  const onFilterClose = useCallback(() => {
    if (
      activeFilter.id === "date_range" &&
      (!activeFilter?.range?.from || !activeFilter?.range?.to)
    ) {
      const { id, type } = options[0];
      const range = getOptionRange(type);
      onChange({ id, range });
    }
    closePopover();
  }, [activeFilter, onChange]);

  const onDatePickerClick = () => {
    if (activeOptionId !== "date_range") {
      onChange({ id: "date_range", range: activeFilter.range });
    }
  };

  const onSelectDateRange = useCallback(
    (range: DateRangeValue | null) => {
      const from = range?.from;
      const to = range?.to;

      if (from && to) {
        onChange({
          id: "date_range",
          range: {
            from: from.startOf("day"),
            to: to.endOf("day"),
          },
        });
        closePopover();
      } else {
        onFilterClose();
      }
    },
    [onChange]
  );

  const renderedOptions = useMemo(
    () =>
      options.map((option) => {
        const range = getOptionRange(option.type);

        return (
          <FormControlLabel
            data-testid={`${id}-${option.id}`}
            key={option.id}
            sx={{ mb: 0.5 }}
            control={
              <Radio
                checked={activeOptionId === option.id}
                onChange={() => handleRadioChange(option)}
                value={option.id}
              />
            }
            label={
              <>
                <Typography variant="body2">{option.title}</Typography>
                {range?.from && range?.to && (
                  <Typography
                    sx={{ mt: 0.25 }}
                    variant="subtitle2"
                    color="grey800"
                  >
                    {`${range.from.format("DD.MM.YYYY")} - 
                    ${range.to.format("DD.MM.YYYY")}`}
                  </Typography>
                )}
              </>
            }
          />
        );
      }),
    [options, activeOptionId]
  );

  return (
    <BaseFilter
      id={id}
      label={label}
      anchorEl={anchorEl}
      openPopover={openPopover}
      onClose={onFilterClose}
    >
      <Box sx={{ p: 2, maxHeight: 350, minWidth: 300, overflowY: "auto" }}>
        <FormGroup>{renderedOptions}</FormGroup>
        <DateRangePicker
          value={datePickerValue}
          onClick={onDatePickerClick}
          onSubmit={onSelectDateRange}
        />
      </Box>
    </BaseFilter>
  );
};

export default React.memo(DateRangeFilter);
